import React, { useEffect, useState } from "react";
import { taxIdState } from "../utils/identity";
import { Supplier, SupplierUpsert } from "../types";
import { purchasingService } from "../services/dataService";
import { poErr } from "../utils/purchasing";
import "../pages/PurchaseOrders.css";

interface Props {
  supplier?: Supplier | null;
  onClose: () => void;
  /** Kaydedilen tedarikçinin kimliği (yeni kayıtta sunucunun döndürdüğü). */
  onSaved: (supplierId?: string) => void;
}

const empty: SupplierUpsert = {
  name: "", code: "", taxNumber: "", taxOffice: "", contactPerson: "", phone: "", email: "",
  address: "", paymentTermDays: 30, currency: "TRY", note: "", isActive: true,
};
const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];
const closeIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>;

const SupplierModal: React.FC<Props> = ({ supplier, onClose, onSaved }) => {
  const [f, setF] = useState<SupplierUpsert>(supplier ? {
    name: supplier.name, code: supplier.code ?? "", taxNumber: supplier.taxNumber ?? "", taxOffice: supplier.taxOffice ?? "",
    contactPerson: supplier.contactPerson ?? "", phone: supplier.phone ?? "", email: supplier.email ?? "", address: supplier.address ?? "",
    paymentTermDays: supplier.paymentTermDays, currency: supplier.currency, note: supplier.note ?? "", isActive: supplier.isActive,
  } : empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose, saving]);

  const set = (k: keyof SupplierUpsert, v: any) => { setF({ ...f, [k]: v }); setError(""); };
  const tid = taxIdState(f.taxNumber ?? "");

  const save = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (saving) return;
    if (!f.name.trim()) return setError("Tedarikçi adı zorunludur.");
    if (tid === "incomplete") return setError("VKN 10 hane, TCKN 11 hane olmalı.");
    if (tid === "invalid") return setError("VKN / TCKN geçersiz; haneleri kontrol edin.");
    setSaving(true); setError("");
    try {
      let id = supplier?.supplierId;
      if (supplier) await purchasingService.updateSupplier(supplier.supplierId, f);
      else id = (await purchasingService.createSupplier(f))?.supplierId;
      onSaved(id); onClose();
    } catch (err: any) {
      setError(poErr(err, "Kaydedilemedi."));
    } finally { setSaving(false); }
  };

  return (
    <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className="po-modal wide" role="dialog" aria-modal="true" aria-labelledby="po-sup-title" onSubmit={save}>
        <div className="po-modal-head">
          <div><span className="po-kicker">Tedarikçi kartı</span><h3 id="po-sup-title">{supplier ? supplier.name : "Yeni tedarikçi"}</h3></div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={saving}>{closeIcon}</button>
        </div>
        <div className="po-modal-body">
          {error && <div className="po-note err" role="alert">{error}</div>}

          <div className="po-form-sec"><h4>Kimlik</h4>
            <div className="po-grid3">
              <label className="po-field span2"><span>Tedarikçi adı<em>zorunlu</em></span><input value={f.name} autoFocus maxLength={200} onChange={(e) => set("name", e.target.value)} /></label>
              <label className="po-field"><span>Kod</span><input value={f.code ?? ""} maxLength={20} onChange={(e) => set("code", e.target.value)} /></label>
              <label className="po-field"><span>VKN / TCKN{tid === "invalid" && <em className="err">geçersiz</em>}{tid === "ok" && <em className="ok">doğrulandı</em>}</span>
                <input className={tid === "invalid" ? "is-invalid" : ""} value={f.taxNumber ?? ""} onChange={(e) => set("taxNumber", e.target.value)} /></label>
              <label className="po-field"><span>Vergi dairesi</span><input value={f.taxOffice ?? ""} onChange={(e) => set("taxOffice", e.target.value)} /></label>
              <label className="po-field"><span>İlgili kişi</span><input value={f.contactPerson ?? ""} onChange={(e) => set("contactPerson", e.target.value)} /></label>
            </div>
          </div>

          <div className="po-form-sec"><h4>İletişim</h4>
            <div className="po-grid3">
              <label className="po-field"><span>Telefon</span><input value={f.phone ?? ""} onChange={(e) => set("phone", e.target.value)} /></label>
              <label className="po-field span2"><span>E-posta</span><input type="email" value={f.email ?? ""} onChange={(e) => set("email", e.target.value)} /></label>
              <label className="po-field span3"><span>Adres</span><input value={f.address ?? ""} maxLength={300} onChange={(e) => set("address", e.target.value)} /></label>
            </div>
          </div>

          <div className="po-form-sec"><h4>Ticari</h4>
            <div className="po-grid3">
              <label className="po-field"><span>Vade<em>gün, 0 = peşin</em></span><input type="number" min={0} max={365} value={f.paymentTermDays} onChange={(e) => set("paymentTermDays", parseInt(e.target.value || "0", 10))} /></label>
              <div className="po-field span2"><span>Para birimi</span>
                <div className="po-seg" role="radiogroup" aria-label="Para birimi">
                  {CURRENCIES.map((c) => <button type="button" key={c} role="radio" aria-checked={f.currency === c} className={f.currency === c ? "on" : ""} onClick={() => set("currency", c)}>{c}</button>)}
                </div>
              </div>
              <label className="po-field span3"><span>Not</span><input value={f.note ?? ""} maxLength={500} onChange={(e) => set("note", e.target.value)} /></label>
              {supplier && (
                <label className="po-check"><input type="checkbox" checked={f.isActive} onChange={(e) => set("isActive", e.target.checked)} /> Aktif tedarikçi</label>
              )}
            </div>
          </div>
        </div>
        <div className="po-modal-foot">
          <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
          <button type="submit" className="po-btn primary" disabled={saving}>{saving ? "Kaydediliyor" : supplier ? "Kaydet" : "Tedarikçiyi oluştur"}</button>
        </div>
      </form>
    </div>
  );
};

export default SupplierModal;
